import useAppointmentEvents from '@hooks/useAppointmentEvents';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import subSeconds from 'date-fns/subSeconds';
import { useEffect, useState } from 'react';
import { PartyStatus } from '@hooks/useOtherPartyStatus';
import { CallStatus } from '@hooks/useCallStatus';
import useMyRole from '@hooks/useMyRole';
import { useRuntimeConfig } from '@vl-core/useRuntimeConfig';

const participantStatus = (status) => {
  return {
    status: (status ? 'connected' : 'disconnected') as PartyStatus
  };
};

const hasPassedRejoinTimeLimit = (apptEndDate: Date, limitInMinutes: number): boolean => {
  if (!apptEndDate || !limitInMinutes) {
    return false;
  }

  const today = new Date();
  const minutesDifference = differenceInMinutes(today, apptEndDate);

  return minutesDifference > limitInMinutes;
};

export const getMostRecentEventStatusMessage = (events: any[], eventType: string) => {
  if (!events || events.length === 0) return null;

  const parsedEvents = events
    .filter((event) => event?.message?.startsWith(eventType))
    .map((event) => {
      try {
        const jsonString = event.message.toString().split('&')[1] || '{}';
        return JSON.parse(jsonString);
      } catch (e) {
        console.error('Failed to parse event JSON:', e, event);
        return null;
      }
    })
    .filter((item) => item !== null);

  if (parsedEvents.length === 0) return null;

  const hasSequences = !Number.isNaN(Number.parseInt(parsedEvents[0]?.SequenceNumber));
  if (!hasSequences) {
    return parsedEvents[0];
  }
  const sortedEvents = [...parsedEvents].sort((a, b) => {
    const seqA = Number.parseInt(a.SequenceNumber);
    const seqB = Number.parseInt(b.SequenceNumber);

    if (!Number.isNaN(seqA) && !Number.isNaN(seqB)) {
      return seqA - seqB;
    }
    return 0;
  });
  return sortedEvents.at(-1);
};

const MAX_DIALING_DURATION_SECONDS = 30;

type AdjustedCallStatusType = {
  status: CallStatus;
  timestamp: Date;
  reason?: string;
};

/**
 * Track party status, call status and appointment delays via a singleton
 * @param appointment
 */
export function useAppointmentStatus(appointment) {
  const myRole = useMyRole();
  const { events } = useAppointmentEvents(appointment);
  const { VIDEO_NO_CASTART_AFTER } = useRuntimeConfig();
  const base = events[events.length - 1] || {};
  const connectedParticipants = getMostRecentEventStatusMessage(
    events,
    'control&{"connectedParticipants"'
  )?.connectedParticipants;
  const controlEvents = events
    .filter(({ message }) => message?.startsWith('control&'))
    .map(({ message, timestamp }) => ({
      control: JSON.parse(message.toString().split('&')[1]),
      timestamp: new Date(timestamp)
    }));
  const completedCallStatus =
    myRole === 'clinician' && controlEvents.find(({ control }) => control?.callStatus === 'completed');
  const callStatus = completedCallStatus || controlEvents.find(({ control }) => control?.callStatus);
  const delayStatus = controlEvents.find(({ control }) => control?.delay);
  const { clin_start, clin_end, appt_end_date } = base;
  const defaultCallStatus = clin_start
    ? {
        status: (clin_end ? 'completed' : 'in-progress') as CallStatus,
        timestamp: new Date(clin_end || clin_start)
      }
    : {
        status: 'not-started' as CallStatus,
        timestamp: new Date()
      };
  // dummy state variable that allows this hook to trigger a re-render
  const [, setRefresh] = useState(0);
  const refreshAppointmentStatus = () => setRefresh((r) => r + 1);
  const dialingStartTime = callStatus?.control.callStatus === 'calling' && new Date(callStatus?.control.timestamp);
  const waitingForPickupDuration = dialingStartTime ? differenceInSeconds(new Date(), dialingStartTime) : undefined;
  const adjustedCallStatus: AdjustedCallStatusType = callStatus
    ? {
        status: callStatus.control.callStatus as CallStatus,
        timestamp: callStatus.timestamp,
        reason: callStatus.control.event
      }
    : defaultCallStatus;

  // when the call is dialing (waiting for the other party to pickup), ensure that the dialing will terminate
  // after 30 seconds
  useEffect(() => {
    if (!Number.isNaN(waitingForPickupDuration) && waitingForPickupDuration <= MAX_DIALING_DURATION_SECONDS) {
      const timer = setTimeout(
        () => refreshAppointmentStatus(),
        (MAX_DIALING_DURATION_SECONDS + 1 - waitingForPickupDuration) * 1000
      );

      return () => clearTimeout(timer);
    }

    return () => {};
  }, [waitingForPickupDuration]);

  // if it ever looks like the dialing has gone on for more that MAX_DIALING_DURATION_SECONDS then return a call status of not-started
  if (callStatus?.control.callStatus === 'calling' && waitingForPickupDuration >= MAX_DIALING_DURATION_SECONDS) {
    adjustedCallStatus.status = 'not-started';
    adjustedCallStatus.timestamp = subSeconds(adjustedCallStatus.timestamp, waitingForPickupDuration * 1000);
    adjustedCallStatus.reason = 'UNANSWERED';
  }

  // if the latest message is a "connectedParticipants" status update
  if (myRole === 'patient' && controlEvents[0]?.control.connectedParticipants) {
    // if I was displaying the ringer
    if (callStatus?.control.callStatus === 'calling') {
      // but the clinician has disappeared, then dispose of the ringer
      if (!connectedParticipants?.clinician) {
        adjustedCallStatus.status = 'not-started';
        adjustedCallStatus.reason = 'CADECLINED';
      }
    }
  }

  // Check if the call was started but not ended. If the time limit for rejoining has passed,
  // mark the status as complete to prevent further rejoining.
  // This check applies only to clinicians to prevent rejoining very old "stuck" appointments.
  // Patients should always be able to return to an active call until the clinician ends it.
  const isPassedRejoinTimeLimit = hasPassedRejoinTimeLimit(appt_end_date, VIDEO_NO_CASTART_AFTER);

  if (myRole === 'clinician' && clin_start && !clin_end && isPassedRejoinTimeLimit) {
    adjustedCallStatus.status = 'completed';
  }

  return {
    base,
    controlEvents,
    callStatus: adjustedCallStatus,
    patientStatus: participantStatus(connectedParticipants?.patient),
    clinicianStatus: participantStatus(connectedParticipants?.clinician),
    delayMins: Number(delayStatus?.control.delay || base.appointment_delay_mins)
  };
}

export default useAppointmentStatus;
